import styled, { keyframes } from 'styled-components';
// ============================================
// THEME CONSTANTS
// ============================================
// These align with the app's CSS custom property system.
// Dark luxury palette for the auction house.
const AUCTION_BG_PRIMARY = 'var(--bg-primary, #1a1a2e)';
const AUCTION_BG_SECONDARY = 'var(--bg-secondary, #16213e)';
const AUCTION_BG_CARD = 'var(--bg-tertiary, #0f3460)';
const AUCTION_GOLD = 'var(--accent-gold, #e6c068)';
const AUCTION_GOLD_LIGHT = 'var(--accent-gold-light, #f0d48a)';
const AUCTION_GOLD_BG = 'var(--accent-gold-bg, rgba(230, 192, 104, 0.08))';
const AUCTION_GOLD_BORDER = 'var(--accent-gold-border, rgba(230, 192, 104, 0.25))';
const AUCTION_TEXT = 'var(--text-primary, #f0f0f0)';
const AUCTION_TEXT_SECONDARY = 'var(--text-secondary, #a0a0b0)';
const AUCTION_TEXT_TERTIARY = 'var(--text-tertiary, #6a6a7a)';
const AUCTION_BORDER = 'var(--border-color, rgba(255, 255, 255, 0.08))';
// ============================================
// PAGE LAYOUT
// ============================================
export const PageContainer = styled.div `
  width: 100%;
  min-height: 100vh;
  background: var(--bg-primary);
`;
export const PageHeader = styled.div `
  padding: 48px 32px 32px;
  text-align: center;
  background: linear-gradient(180deg, ${AUCTION_BG_SECONDARY} 0%, ${AUCTION_BG_PRIMARY} 100%);
  border-bottom: 1px solid ${AUCTION_BORDER};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;

  > div {
    flex: 1;
    text-align: center;
  }

  @media (max-width: 768px) {
    padding: 32px 16px 24px;
    flex-direction: column;
    gap: 1rem;
  }
`;
export const Title = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 38px;
  font-weight: 600;
  letter-spacing: 0.03em;
  margin-bottom: 10px;
  color: ${AUCTION_TEXT};

  span {
    color: ${AUCTION_GOLD};
  }

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;
export const Subtitle = styled.p `
  color: ${AUCTION_TEXT_SECONDARY};
  font-size: 15px;
  max-width: 480px;
  margin: 0 auto;
`;
// ============================================
// TAB BAR
// ============================================
export const TabBar = styled.div `
  border-bottom: 1px solid ${AUCTION_BORDER};
  display: flex;
  gap: 0;

  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;
export const TabButton = styled.button `
  padding: 14px 20px;
  color: ${props => props.$active ? AUCTION_GOLD : AUCTION_TEXT_SECONDARY};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? AUCTION_GOLD : 'transparent'};
  background: transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;

  &:hover {
    color: ${AUCTION_TEXT};
    background: ${AUCTION_BG_SECONDARY};
  }

  svg {
    width: 14px;
    height: 14px;
  }

  @media (max-width: 600px) {
    padding: 12px 10px;
    font-size: 12px;
    border-bottom: 1px solid ${AUCTION_BORDER};
    border-right: 1px solid ${AUCTION_BORDER};

    &:nth-child(2n) {
      border-right: none;
    }

    &:nth-child(n+3) {
      border-bottom: 2px solid ${props => props.$active ? AUCTION_GOLD : 'transparent'};
    }
  }
`;
// ============================================
// CONTENT AREA (contained, centered)
// ============================================
export const ContentArea = styled.div `
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 32px 48px;
  box-sizing: border-box;

  @media (max-width: 1400px) {
    max-width: 1400px;
    padding: 0 24px 48px;
  }

  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 0 20px 32px;
  }

  @media (max-width: 768px) {
    padding: 0 16px 32px;
  }

  @media (max-width: 375px) {
    padding: 0 12px 24px;
  }
`;
// ============================================
// FILTER BAR
// ============================================
export const FilterBar = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;
export const FilterSelect = styled.select `
  background: ${AUCTION_BG_PRIMARY};
  border: 1px solid ${AUCTION_BORDER};
  padding: 10px 14px;
  color: ${AUCTION_TEXT};
  font-size: 13px;
  cursor: pointer;
  border-radius: 8px;
  min-width: 140px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${AUCTION_GOLD};
    box-shadow: 0 0 0 3px ${AUCTION_GOLD_BG};
  }

  option {
    background: ${AUCTION_BG_PRIMARY};
    color: ${AUCTION_TEXT};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;
export const SearchInput = styled.input `
  flex: 1;
  background: ${AUCTION_BG_SECONDARY};
  border: 1px solid ${AUCTION_BORDER};
  padding: 10px 16px;
  color: ${AUCTION_TEXT};
  font-size: 13px;
  border-radius: 8px;
  transition: all 0.2s;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: ${AUCTION_GOLD};
    box-shadow: 0 0 0 3px ${AUCTION_GOLD_BG};
  }

  &::placeholder {
    color: ${AUCTION_TEXT_TERTIARY};
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
  }
`;
export const RefreshButton = styled.button `
  background: transparent;
  border: 1px solid ${AUCTION_BORDER};
  color: ${AUCTION_TEXT_SECONDARY};
  padding: 10px 16px;
  font-size: 13px;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    border-color: ${AUCTION_GOLD};
    color: ${AUCTION_GOLD};
    background: ${AUCTION_GOLD_BG};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;
// ============================================
// AUCTION GRID
// ============================================
export const AuctionGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;
// ============================================
// PAGINATION
// ============================================
export const PaginationContainer = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px 0 12px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 6px;
    padding: 16px 0 8px;
  }
`;
export const PaginationButton = styled.button `
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background: ${props => props.$active ? AUCTION_GOLD : 'transparent'};
  border: 1px solid ${props => props.$active ? AUCTION_GOLD : AUCTION_BORDER};
  color: ${props => props.$active ? '#1a1a2e' : AUCTION_TEXT_SECONDARY};
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: ${AUCTION_GOLD};
    color: ${props => props.$active ? '#1a1a2e' : AUCTION_GOLD};
    background: ${props => props.$active ? AUCTION_GOLD_LIGHT : AUCTION_GOLD_BG};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;
export const PaginationInfo = styled.span `
  color: ${AUCTION_TEXT_SECONDARY};
  font-size: 13px;
  padding: 0 8px;
`;
// ============================================
// EMPTY STATE
// ============================================
export const EmptyState = styled.div `
  padding: 64px 24px;
  text-align: center;
  color: ${AUCTION_TEXT_TERTIARY};
  border: 1px dashed ${AUCTION_BORDER};
  border-radius: 8px;
  font-size: 15px;
  line-height: 1.6;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    color: ${AUCTION_TEXT_TERTIARY};
    opacity: 0.5;
  }

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 18px;
    color: ${AUCTION_TEXT_SECONDARY};
    margin-bottom: 8px;
  }
`;
// ============================================
// LOADING SPINNER
// ============================================
const spin = keyframes `
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
export const LoadingSpinner = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  color: ${AUCTION_TEXT_SECONDARY};
  font-size: 14px;
  gap: 16px;

  &::before {
    content: '';
    width: 36px;
    height: 36px;
    border: 3px solid ${AUCTION_BORDER};
    border-top-color: ${AUCTION_GOLD};
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  }
`;
// ============================================
// ERROR STATE
// ============================================
export const ErrorState = styled.div `
  padding: 48px 24px;
  text-align: center;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  color: #ef4444;
  font-size: 14px;

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 18px;
    margin-bottom: 8px;
  }

  button {
    margin-top: 16px;
    padding: 10px 24px;
    background: transparent;
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    cursor: pointer;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      background: rgba(239, 68, 68, 0.1);
      border-color: #ef4444;
    }
  }
`;
// ============================================
// RESULTS HEADER
// ============================================
export const ResultsHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;
export const ResultsCount = styled.div `
  font-size: 13px;
  color: ${AUCTION_TEXT_SECONDARY};

  strong {
    color: ${AUCTION_GOLD};
    font-weight: 600;
  }
`;
